import { selectLatestVendorInvite } from './selectLatestVendorInvite'
import { type VendorInvite } from '../../data-access/types/VendorInvite'
import {
  type VendorInviteStatusEnum,
  VendorInviteStatuses,
} from '../../entities/enums/VendorInviteStatusEnum'

// The invites whose link a vendor can still use to start signing up. ACCEPTED
// is left out: the vendor is already signing up through it, so neither a
// cancel nor a resend may retire it.
export const PENDING_STATUSES: VendorInviteStatusEnum[] = [
  VendorInviteStatuses.SENT,
  VendorInviteStatuses.VIEWED,
]

export interface VendorPortalConnection {
  isConnectedToPortal: boolean
  /**
   * A connection existed once but no longer does. History may exist, so
   * callers keep the thread reachable read-only instead of hiding it behind
   * the "invite them" hint (VND-108 AC14).
   */
  wasConnectedToPortal: boolean
  /**
   * The row the pane's status pill, onboarding summary and actions read. A
   * CONNECTED row wins over newer ones: a later invite to another contact that
   * was cancelled or is still in flight does not change that the vendor is on
   * the portal. Otherwise the newest row, where this vendor stands now.
   */
  currentInvite: Nullable<VendorInvite>
  pendingInvite: Nullable<VendorInvite>
}

/** Where a vendor stands with the portal, derived from its invite rows. */
export const getVendorPortalConnection = (
  invites: VendorInvite[]
): VendorPortalConnection => {
  // Deliberately a reduction over every row rather than the latest invite's
  // status: VendorOverview gates portal-sourced tax details on this flag, and
  // that data stays valid while a newer invite row is mid-flight.
  const isConnectedToPortal = invites.some(
    (invite) => invite.status === VendorInviteStatuses.CONNECTED
  )

  return {
    isConnectedToPortal,
    wasConnectedToPortal:
      !isConnectedToPortal &&
      invites.some(
        (invite) => invite.status === VendorInviteStatuses.DISCONNECTED
      ),
    currentInvite:
      selectLatestVendorInvite(
        invites.filter(
          (invite) => invite.status === VendorInviteStatuses.CONNECTED
        )
      ) ?? selectLatestVendorInvite(invites),
    pendingInvite: selectLatestVendorInvite(
      invites.filter((invite) => PENDING_STATUSES.includes(invite.status))
    ),
  }
}
